import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { isNative } from '@/lib/native';
import './App.css';

import Landing from '@/pages/Landing';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Disclaimer from '@/pages/Disclaimer';
import Contact from '@/pages/Contact';
import Verify from '@/pages/Verify';
import Login from '@/pages/auth/Login';
import PhoneLogin from '@/pages/auth/PhoneLogin';
import EmployeeLogin from '@/pages/auth/EmployeeLogin';
import Register from '@/pages/auth/Register';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import SuperAdminLogin from '@/pages/auth/SuperAdminLogin';
import SuperAdminDashboard from '@/pages/admin/SuperAdminDashboard';
import Splash from '@/pages/app/Splash';
import Categories from '@/pages/app/Categories';
import SubCategory from '@/pages/app/SubCategory';
import TravelSubCategory from '@/pages/app/TravelSubCategory';
import HotelSubCategory from '@/pages/app/HotelSubCategory';
import Capture from '@/pages/app/Capture';
import Editor from '@/pages/app/Editor';
import PayNow from '@/pages/app/PayNow';
import BillGen from '@/pages/app/BillGen';
import Dashboard from '@/pages/app/Dashboard';
import Wallet from '@/pages/app/Wallet';
import Trips from '@/pages/app/Trips';
import Reports from '@/pages/app/Reports';
import Profile from '@/pages/app/Profile';
import Referrals from '@/pages/app/Referrals';
import CompanyDashboard from '@/pages/app/CompanyDashboard';
import WalletPinSetup from '@/pages/app/WalletPinSetup';
import AppShell from '@/components/AppShell';


const RootEntry = () => {
  const { user } = useAuth();
  if (!isNative()) return <Landing />;
  return <Navigate to={user ? '/app' : '/login'} replace />;
};

const Private = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
};

const AppAccess = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.user_type !== 'corporate' && !user.wallet_pin_set) return <Navigate to="/wallet-pin" replace />;
  return children;
};

const SuperAdminGuard = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/superadmin/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<RootEntry />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/verify/:billId" element={<Verify />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/phone" element={<PhoneLogin />} />
          <Route path="/login/employee" element={<EmployeeLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/wallet-pin" element={<Private><WalletPinSetup /></Private>} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/superadmin/login" element={<SuperAdminLogin />} />
          <Route path="/superadmin" element={<SuperAdminGuard><SuperAdminDashboard /></SuperAdminGuard>} />

          <Route path="/app" element={<AppAccess><AppShell /></AppAccess>}>
            <Route index element={<Splash />} />
            <Route path="company" element={<CompanyDashboard />} />
            <Route path="categories" element={<Categories />} />
            <Route path="category/travel" element={<TravelSubCategory />} />
            <Route path="category/hotel" element={<HotelSubCategory />} />
            <Route path="category/:cat" element={<SubCategory />} />
            <Route path="capture/:cat/:sub" element={<Capture />} />
            <Route path="editor" element={<Editor />} />
            <Route path="pay" element={<PayNow />} />
            <Route path="bill/:id" element={<BillGen />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="trips" element={<Trips />} />
            <Route path="reports" element={<Reports />} />
            <Route path="profile" element={<Profile />} />
            <Route path="referrals" element={<Referrals />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
